import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, DollarSign, Languages, BarChart3, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const AdminPanel = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  const [website, setWebsite] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currencies, setCurrencies] = useState(['USD']);
  const [languages, setLanguages] = useState(['en']);
  const [newCurrency, setNewCurrency] = useState('');
  const [newLanguage, setNewLanguage] = useState('');

  useEffect(() => {
    fetchWebsite();
    fetchAnalytics();
  }, [websiteId]);

  const fetchWebsite = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setWebsite(data);
      setCurrencies(data.currencies || ['USD']);
      setLanguages(data.languages || ['en']);
    } catch (error) {
      toast.error('Failed to load website');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/websites/${websiteId}/analytics`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics');
    }
  };

  const updateWebsite = async (updates) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/websites/${websiteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        toast.success('Settings updated successfully');
        fetchWebsite();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const addCurrency = () => {
    if (newCurrency && !currencies.includes(newCurrency.toUpperCase())) {
      const updated = [...currencies, newCurrency.toUpperCase()];
      setCurrencies(updated);
      updateWebsite({ currencies: updated });
      setNewCurrency('');
    }
  };

  const removeCurrency = (currency) => {
    const updated = currencies.filter(c => c !== currency);
    if (updated.length > 0) {
      setCurrencies(updated);
      updateWebsite({ currencies: updated });
    }
  };

  const addLanguage = () => {
    if (newLanguage && !languages.includes(newLanguage.toLowerCase())) {
      const updated = [...languages, newLanguage.toLowerCase()];
      setLanguages(updated);
      updateWebsite({ languages: updated });
      setNewLanguage('');
    }
  };

  const removeLanguage = (language) => {
    const updated = languages.filter(l => l !== language);
    if (updated.length > 0) {
      setLanguages(updated);
      updateWebsite({ languages: updated });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-zinc-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="admin-panel-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl md:text-5xl font-heading font-extrabold mb-2" data-testid="admin-title">
            {website?.name}
          </h1>
          <p className="text-zinc-400" data-testid="admin-subtitle">
            Manage settings, analytics, and multi-currency options
          </p>
        </div>

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="bg-zinc-900 p-1 rounded-xl">
            <TabsTrigger value="analytics" data-testid="tab-analytics" className="data-[state=active]:bg-violet-500">
              <BarChart3 className="w-4 h-4 mr-2" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="currencies" data-testid="tab-currencies" className="data-[state=active]:bg-violet-500">
              <DollarSign className="w-4 h-4 mr-2" />
              Currencies
            </TabsTrigger>
            <TabsTrigger value="languages" data-testid="tab-languages" className="data-[state=active]:bg-violet-500">
              <Languages className="w-4 h-4 mr-2" />
              Languages
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" data-testid="analytics-content">
            <div className="glass p-8 rounded-2xl">
              <h2 className="text-2xl font-heading font-bold mb-6">Website Analytics</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-zinc-900/50 p-6 rounded-xl" data-testid="total-views-card">
                  <div className="flex items-center gap-3 mb-2">
                    <Globe className="w-6 h-6 text-accent" />
                    <span className="text-sm text-zinc-400">Total Views</span>
                  </div>
                  <p className="text-4xl font-heading font-bold" data-testid="total-views-count">
                    {analytics?.total_views || 0}
                  </p>
                </div>
                <div className="bg-zinc-900/50 p-6 rounded-xl" data-testid="unique-visitors-card">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-6 h-6 text-accent" />
                    <span className="text-sm text-zinc-400">Unique Visitors</span>
                  </div>
                  <p className="text-4xl font-heading font-bold" data-testid="unique-visitors-count">
                    {analytics?.unique_visitors || 0}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-bold mb-4">Recent Events</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {analytics?.events?.length > 0 ? (
                    analytics.events.map((event, index) => (
                      <div
                        key={index}
                        data-testid={`event-${index}`}
                        className="bg-zinc-900/50 p-4 rounded-xl text-sm"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-zinc-400">{event.event_type}</span>
                          <span className="text-zinc-500 text-xs">{new Date(event.timestamp).toLocaleString()}</span>
                        </div>
                        {event.page_url && (
                          <p className="text-zinc-500 text-xs mt-1 truncate">{event.page_url}</p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-400 text-center py-8" data-testid="no-events">No events tracked yet</p>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="currencies" data-testid="currencies-content">
            <div className="glass p-8 rounded-2xl">
              <h2 className="text-2xl font-heading font-bold mb-6">Multi-Currency Settings</h2>
              
              <div className="mb-6">
                <Label className="text-white mb-2 block">Active Currencies</Label>
                <div className="flex flex-wrap gap-2">
                  {currencies.map((currency) => (
                    <div
                      key={currency}
                      data-testid={`currency-${currency}`}
                      className="bg-violet-500/20 border border-violet-500 px-4 py-2 rounded-full flex items-center gap-2"
                    >
                      <span className="font-mono font-bold">{currency}</span>
                      {currencies.length > 1 && (
                        <button
                          onClick={() => removeCurrency(currency)}
                          data-testid={`remove-currency-${currency}`}
                          className="text-zinc-400 hover:text-white"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Input
                  placeholder="USD, EUR, GBP..."
                  value={newCurrency}
                  onChange={(e) => setNewCurrency(e.target.value)}
                  data-testid="currency-input"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addCurrency()}
                />
                <Button
                  onClick={addCurrency}
                  data-testid="add-currency-button"
                  className="bg-violet-500 hover:bg-violet-600 text-white rounded-xl px-6"
                >
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="languages" data-testid="languages-content">
            <div className="glass p-8 rounded-2xl">
              <h2 className="text-2xl font-heading font-bold mb-6">Multi-Language Settings</h2>
              
              <div className="mb-6">
                <Label className="text-white mb-2 block">Active Languages</Label>
                <div className="flex flex-wrap gap-2">
                  {languages.map((language) => (
                    <div
                      key={language}
                      data-testid={`language-${language}`}
                      className="bg-accent/20 border border-accent px-4 py-2 rounded-full flex items-center gap-2"
                    >
                      <span className="font-mono font-bold">{language}</span>
                      {languages.length > 1 && (
                        <button
                          onClick={() => removeLanguage(language)}
                          data-testid={`remove-language-${language}`}
                          className="text-zinc-400 hover:text-white"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Input
                  placeholder="en, es, fr..."
                  value={newLanguage}
                  onChange={(e) => setNewLanguage(e.target.value)}
                  data-testid="language-input"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
                  onKeyPress={(e) => e.key === 'Enter' && addLanguage()}
                />
                <Button
                  onClick={addLanguage}
                  data-testid="add-language-button"
                  className="bg-accent hover:bg-accent/80 text-black rounded-xl px-6 font-bold"
                >
                  Add
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;